import { format } from "date-fns";
import { startCase } from "lodash";
import toast from "react-hot-toast";
import { useNavigate } from "react-router";
import { useUtils } from "@/app/hooks/useUtils";
import { useLoader } from "@/app/hooks/useLoader";
import { useRouter } from "@/app/hooks/useRouter";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { useEffect, useMemo, useState } from "react";
import { useAppDispatch } from "@/app_redux/hooks/root_hook";
import PaginationComponent from "@/app/components/ui/Pagination";
import { useImageConstants } from "@/app/hooks/useImageConstants";
import { useMappedConstants } from "@/app/hooks/useMappedConstants";
import NoRecordFoundComponent from "@/app/components/ui/NoRecordFound";
import TableSkeletonComponent from "@/app/components/ui/LoaderComponent";
import { IRotaOverbookingRequest } from "@/api_framework/api_modals/Rota";
import { PencilSquareIcon, TrashIcon } from "@heroicons/react/24/outline";
import { useRotaUpdateImpactAPI } from "@/app/hooks/api_hooks/Rota/useRotaUpdationWarnAPI";
import { IRotaState, setOverbookingRequestFilters } from "@/app_redux/reducers/slices/auth/rota_slice";
import OverbookingConfirmationModal from "@/app/components/routes/modals/OverbookingConfirmationModal";
import { useRotaOverbookingRequestsAPI } from "@/app/hooks/api_hooks/Rota/useRotaOverbookingRequestsAPI";
import { useRotaOverbookingBulkUploadAPI } from "@/app/hooks/api_hooks/Rota/useRotaOverbookingBulkUploadAPI";
import AppointmentOverBookingV2FilterComponent, { IAppointmentOverBookingV2FilterFields } from "./FilterComponent";
import { useRotaOverbookingRequestDeleteAPI } from "@/app/hooks/api_hooks/Rota/useRotaOverbookingRequestDeleteAPI";

const DEFAULT_FILTERS: IAppointmentOverBookingV2FilterFields = {
  clinicId: "",
  clinicName: "",
  type: "",
  date: "",
};

const PAGE_SIZE = 20;

const TABLE_HEADERS = [
  { item: "Created Date" },
  { item: "Clinic" },
  { item: "OB Date" },
  { item: "Type" },
  { item: "Staff" },
  { item: "Shift" },
  { item: "State" },
  { item: "Created By" },
  { item: "Actions" },
];
//test

const getRequestId = (request: IRotaOverbookingRequest | null) => `${request?.id ?? request?.request_id ?? ""}`;

const getRequestMeta = (request?: IRotaOverbookingRequest | null) => {
  const requestData = request as any;
  const status = `${requestData?.status ?? requestData?.state ?? ""}`.trim().toLowerCase();
  return {
    id: requestData?.id ?? requestData?.request_id,
    createdDate: requestData?.created_at || requestData?.created_date,
    clinicName:
      requestData?.clinic_details?.name ||
      requestData?.clinic_name ||
      requestData?.clinic?.name ||
      request?.clinic_id ||
      "-",
    obDate: requestData?.date || requestData?.request_date || requestData?.ob_date,
    type: requestData?.service_type || requestData?.type || "-",
    staffName: requestData?.staff_details?.name || requestData?.staff_name || requestData?.staff?.name || "-",
    shiftName:
      requestData?.shift_details?.transformed_name ||
      requestData?.shift_details?.name ||
      requestData?.shift_name ||
      request?.shift_id ||
      "-",
    createdBy: requestData?.created_by || requestData?.created_by_name || requestData?.created_by?.name || "-",
    status,
    clinicId: requestData?.clinic_details?.id || request?.clinic_id || "",
    staffId: requestData?.staff_details?.id || requestData?.staff_id || requestData?.staff?.id || "",
    rotaDate: requestData?.date || requestData?.request_date || requestData?.ob_date || "",
    rotaId: requestData?.rota_details?.id || "",
  };
};

const AppointmentOverBookingV2Component = () => {
  const dispatch = useAppDispatch();
  const { getRouteKey } = useRouter();
  const { bookingStatusesColors } = useMappedConstants();
  const routeId = getRouteKey("ROTA", "id");
  const { routeState } = useRouteInfo(routeId);
  const navigate = useNavigate();
  const { NO_PWL_RESULTS } = useImageConstants();
  const { veticDateFormat, truncateString } = useUtils();
  const isOverbookingRequestLoading = useLoader("isOverbookingRequestsInProgress");
  const isOverbookingRequestMutationInProgress = useLoader("isOverbookingRequestMutationInProgress");
  const isOverbookingBulkUploadInProgress = useLoader("isOverbookingBulkUploadInProgress");
  const fetchOverbookingRequests = useRotaOverbookingRequestsAPI();
  const uploadOverbookingRequests = useRotaOverbookingBulkUploadAPI();
  const deleteOverbookingRequest = useRotaOverbookingRequestDeleteAPI();
  const { execute: warnOverbookingImpact, loading: isDeleteWarnInProgress } = useRotaUpdateImpactAPI();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<IRotaOverbookingRequest | null>(null);
  const [deleteImpactCount, setDeleteImpactCount] = useState<number | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const rotaState: Partial<IRotaState> = routeState?.state ?? {};
  const overbookingRequests = rotaState.overbookingRequests ?? [];
  const totalOverbookingRequests = rotaState.overbookingRequestsTotalCount ?? 0;
  const overbookingRequestFilters = rotaState.overbookingRequestFilters ?? null;

  const filters = useMemo(
    () => ({
      ...DEFAULT_FILTERS,
      ...(overbookingRequestFilters ?? {}),
    }),
    [
      overbookingRequestFilters?.clinicId,
      overbookingRequestFilters?.clinicName,
      overbookingRequestFilters?.type,
      overbookingRequestFilters?.date,
    ],
  );

  const getOverbookingRequestPayload = (currentFilters: IAppointmentOverBookingV2FilterFields, page = currentPage) => ({
    clinic_ids: currentFilters?.clinicId || undefined,
    service_type: currentFilters?.type || undefined,
    from_date: currentFilters?.date || undefined,
    to_date: currentFilters?.date || undefined,
    page,
    page_size: PAGE_SIZE,
  });

  const buildFilterQuery = () => {
    const query = new URLSearchParams();
    if (filters?.clinicId) query.set("clinicId", filters.clinicId);
    if (filters?.type) query.set("type", filters.type);
    const queryString = query.toString();
    return queryString ? `?${queryString}` : "";
  };

  const getListUrl = () => `${getRouteKey("OVERBOOKING_V2", "url")}${buildFilterQuery()}`;

  const getStatusClassName = (status?: string | null) => {
    const normalized = `${status ?? ""}`.trim().toLowerCase();
    if (normalized === "deleted") return "bg-red-50 text-red-600";
    if (normalized === "raised") return "bg-yellow-50 text-yellow-700";
    return bookingStatusesColors?.[normalized] || "bg-gray-100 text-gray-700";
  };

  const handleFilterChange = (newFilters: IAppointmentOverBookingV2FilterFields) => {
    const hasFiltersChanged =
      filters?.clinicId !== (newFilters?.clinicId ?? "") ||
      filters?.clinicName !== (newFilters?.clinicName ?? "") ||
      filters?.type !== (newFilters?.type ?? "") ||
      filters?.date !== (newFilters?.date ?? "");
    const nextPage = hasFiltersChanged ? 1 : currentPage;
    if (hasFiltersChanged) {
      setCurrentPage(1);
    }
    dispatch(
      setOverbookingRequestFilters({
        data: newFilters,
      }),
    );
    if (!hasFiltersChanged) {
      fetchOverbookingRequests(getOverbookingRequestPayload(newFilters, nextPage));
    }
  };

  const handleDialogClose = () => {
    setIsDeleteModalOpen(false);
    setSelectedRequest(null);
    setDeleteImpactCount(null);
  };

  const refreshOverbookingRequests = (page = currentPage) => {
    fetchOverbookingRequests(getOverbookingRequestPayload(filters, page));
  };

  const handleUploadRequests = async (file: File | null) => {
    if (!file) return;
    const lowerName = file.name?.toLowerCase?.() || "";
    const isValidExcelFile = lowerName.endsWith(".xls") || lowerName.endsWith(".xlsx");
    if (!isValidExcelFile) {
      toast.error("Please upload a valid .xls or .xlsx file.");
      return;
    }
    const response = await uploadOverbookingRequests(file);
    if (response?.status) {
      refreshOverbookingRequests();
    }
  };

  useEffect(() => {
    if (!overbookingRequestFilters) {
      dispatch(
        setOverbookingRequestFilters({
          data: DEFAULT_FILTERS,
        }),
      );
      return;
    }
    const hasOnlyLegacyAutoDate =
      !overbookingRequestFilters?.clinicId &&
      !overbookingRequestFilters?.clinicName &&
      !overbookingRequestFilters?.type &&
      overbookingRequestFilters?.date === format(new Date(), "yyyy-MM-dd");
    if (!hasOnlyLegacyAutoDate) return;
    dispatch(
      setOverbookingRequestFilters({
        data: { date: "" },
      }),
    );
  }, [
    dispatch,
    overbookingRequestFilters?.clinicId,
    overbookingRequestFilters?.clinicName,
    overbookingRequestFilters?.type,
    overbookingRequestFilters?.date,
  ]);

  useEffect(() => {
    return () => {
      dispatch(
        setOverbookingRequestFilters({
          data: null,
        }),
      );
    };
  }, [dispatch]);

  useEffect(() => {
    fetchOverbookingRequests(getOverbookingRequestPayload(filters, currentPage));
  }, [filters?.clinicId, filters?.type, filters?.date, currentPage]);

  useEffect(() => {
    if (isOverbookingRequestLoading || totalOverbookingRequests <= 0) return;
    const totalPages = Math.max(1, Math.ceil(totalOverbookingRequests / PAGE_SIZE));
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, isOverbookingRequestLoading, totalOverbookingRequests]);

  const handleNewRequest = () => {
    navigate(`${getRouteKey("OVERBOOKING_V2", "url")}/create`, {
      state: {
        listUrl: getListUrl(),
      },
    });
  };

  const handleUpdateRequest = (request: IRotaOverbookingRequest) => {
    const requestId = getRequestId(request);
    if (!requestId) return;
    navigate(`${getRouteKey("OVERBOOKING_V2", "url")}/update`, {
      state: {
        requestData: request,
        initialFilters: {
          clinicId: filters?.clinicId || "",
          type: filters?.type || "",
          date: filters?.date || "",
        },
        listUrl: getListUrl(),
      },
    });
  };

  const handleDeleteRequest = (request: IRotaOverbookingRequest) => {
    setSelectedRequest(request);
    setIsDeleteModalOpen(true);
    setDeleteImpactCount(null);
    const requestMeta = getRequestMeta(request);
    if (!requestMeta.clinicId || !requestMeta.staffId || !requestMeta.rotaDate || !requestMeta.rotaId) return;
    warnOverbookingImpact({
      clinic_id: requestMeta.clinicId,
      staff_id: requestMeta.staffId,
      rota_date: requestMeta.rotaDate,
      rota_id: requestMeta.rotaId,
    }).then((response) => {
      if (response?.status) {
        setDeleteImpactCount(response?.data?.booking_count ?? 0);
      } else {
        setDeleteImpactCount(null);
      }
    });
  };

  const confirmDeleteRequest = async () => {
    const requestId = getRequestId(selectedRequest);
    if (!requestId || isOverbookingRequestMutationInProgress) return;
    const isDeleted = await deleteOverbookingRequest(requestId);
    if (!isDeleted) return;
    setIsDeleteModalOpen(false);
    setSelectedRequest(null);
    setDeleteImpactCount(null);
    refreshOverbookingRequests();
  };

  return (
    <div className="px-3 py-3">
      <div className="relative z-30 bg-white rounded-lg border border-gray-100 shadow-sm p-3">
        <AppointmentOverBookingV2FilterComponent
          initialFilters={filters}
          onFiltersChange={handleFilterChange}
          onNewClick={handleNewRequest}
          onUploadClick={handleUploadRequests}
          isUploadInProgress={isOverbookingBulkUploadInProgress}
        />
      </div>
      <div className="mt-3 relative z-0">
        {!isOverbookingRequestLoading && totalOverbookingRequests > 0 && (
          <div className="flex justify-between mb-2">
            <p className="text-slate-400 text-xs font-normal mt-2 ml-2">
              Showing {totalOverbookingRequests === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1} -{" "}
              {Math.min(currentPage * PAGE_SIZE, totalOverbookingRequests)} of {totalOverbookingRequests} results
            </p>
          </div>
        )}
        {isOverbookingRequestLoading ? (
          <TableSkeletonComponent columnLength={9} bodyLength={8} />
        ) : overbookingRequests && overbookingRequests?.length > 0 ? (
          <div className="rounded-2xl overflow-x-auto">
            <table className="table-auto w-full border-separate border-spacing-x-0 border-spacing-y-0.5 bg-[#F5F5F5] rounded-xl px-[3px] pb-[1px]">
              <thead className="bg-[#F5F5F5] text-[#1F1F23] font-normal sticky top-0 z-10">
                <tr>
                  {TABLE_HEADERS.map((key, index) => (
                    <th
                      key={index}
                      scope="col"
                      className={`px-3 py-3 text-left text-xs font-medium text-black capitalize tracking-wider whitespace-nowrap ${
                        key.item === "Actions" ? "sm:sticky right-0 bg-[#F5F5F5] z-10 text-center min-w-[96px]" : ""
                      }`}
                    >
                      {key.item}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(overbookingRequests as IRotaOverbookingRequest[])?.map((request, index, arr) => {
                  const requestMeta = getRequestMeta(request);
                  const isFirst = index === 0;
                  const isLast = index === arr.length - 1;
                  const isSingle = arr.length === 1;
                  const canUpdateRequest = requestMeta.status !== "deleted";
                  const canDeleteRequest = requestMeta.status !== "deleted";
                  return (
                    <tr key={requestMeta.id ?? index} className="bg-white">
                      <td
                        className={`px-3 py-3 text-xs font-normal text-black align-middle whitespace-nowrap ${
                          isSingle ? "rounded-tl-2xl rounded-bl-2xl" : ""
                        } ${isFirst && !isSingle ? "rounded-tl-2xl" : ""} ${isLast && !isSingle ? "rounded-bl-2xl" : ""}`}
                      >
                        {veticDateFormat(requestMeta.createdDate) || "-"}
                      </td>
                      <td className="px-3 py-3 text-xs font-normal text-black align-middle min-w-[160px] max-w-[220px]">
                        <p className="truncate" title={requestMeta.clinicName}>
                          {truncateString(requestMeta.clinicName, 28)}
                        </p>
                      </td>
                      <td className="px-3 py-3 text-xs font-normal text-black align-middle whitespace-nowrap">
                        {veticDateFormat(requestMeta.obDate) || "-"}
                      </td>
                      <td className="px-3 py-3 text-xs font-normal text-black align-middle whitespace-nowrap">
                        {requestMeta.type}
                      </td>
                      <td className="px-3 py-3 text-xs font-normal text-black align-middle min-w-[140px] max-w-[200px]">
                        <p className="truncate" title={requestMeta.staffName}>
                          {truncateString(requestMeta.staffName, 24)}
                        </p>
                      </td>
                      <td className="px-3 py-3 text-xs font-normal text-black align-middle min-w-[160px] max-w-[240px]">
                        <p className="truncate" title={requestMeta.shiftName}>
                          {truncateString(requestMeta.shiftName, 30)}
                        </p>
                      </td>
                      <td className="px-3 py-3 text-xs font-normal text-black align-middle whitespace-nowrap">
                        {!requestMeta.status || requestMeta.status === "-" ? (
                          "-"
                        ) : (
                          <div
                            className={`inline-flex min-w-[78px] items-center justify-center gap-1.5 px-3 py-1.5 rounded-full ${getStatusClassName(
                              requestMeta.status,
                            )}`}
                          >
                            <span className="text-xs font-normal">{startCase(requestMeta.status)}</span>
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-3 text-xs font-normal text-gray-500 align-middle min-w-[140px] max-w-[200px]">
                        <p className="truncate" title={requestMeta.createdBy}>
                          {truncateString(requestMeta.createdBy, 24)}
                        </p>
                      </td>
                      <td
                        className={`px-3 py-3 sm:sticky right-0 bg-gray-50 z-[5] align-middle whitespace-nowrap min-w-[96px] ${
                          isSingle ? "rounded-tr-2xl rounded-br-2xl" : ""
                        } ${isFirst && !isSingle ? "rounded-tr-2xl" : ""} ${isLast && !isSingle ? "rounded-br-2xl" : ""}`}
                      >
                        <div className="flex items-center justify-end gap-2">
                          {canUpdateRequest ? (
                            <button
                              type="button"
                              onClick={() => handleUpdateRequest(request)}
                              title="Update"
                              aria-label="Update request"
                              className="w-8 h-8 shrink-0 inline-flex items-center justify-center rounded-md bg-[#F1F1F1] hover:bg-gray-200 transition-colors"
                            >
                              <PencilSquareIcon className="w-4 h-4 text-gray-700" />
                            </button>
                          ) : null}
                          {canDeleteRequest ? (
                            <button
                              type="button"
                              onClick={() => handleDeleteRequest(request)}
                              title="Delete"
                              aria-label="Delete request"
                              className="w-8 h-8 shrink-0 inline-flex items-center justify-center rounded-md bg-red-50 hover:bg-red-100 transition-colors"
                            >
                              <TrashIcon className="w-4 h-4 text-red-600" />
                            </button>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="rounded-2xl overflow-x-auto">
            <div className="w-full bg-[#F5F5F5] rounded-xl px-[3px] pb-[1px]">
              <div className="bg-white rounded-2xl min-h-[220px] flex items-center justify-center">
                <NoRecordFoundComponent imageURL={NO_PWL_RESULTS} message="No Overbooking Requests Found" />
              </div>
            </div>
          </div>
        )}
        {!isOverbookingRequestLoading && totalOverbookingRequests > 0 && (
          <div className="mt-3 pb-4">
            <PaginationComponent
              className="pagination-bar"
              currentPage={currentPage}
              totalCount={totalOverbookingRequests || 0}
              pageSize={PAGE_SIZE}
              onPageChange={(pageNumber) => {
                setCurrentPage(pageNumber);
              }}
            />
          </div>
        )}
      </div>
      <OverbookingConfirmationModal
        open={isDeleteModalOpen}
        variant="delete_overbooking_request"
        onClose={handleDialogClose}
        impactCount={deleteImpactCount}
        onConfirm={confirmDeleteRequest}
        confirmDisabled={isOverbookingRequestMutationInProgress || isDeleteWarnInProgress}
        cancelDisabled={isOverbookingRequestMutationInProgress || isDeleteWarnInProgress}
        confirmText={
          isDeleteWarnInProgress ? "Checking..." : isOverbookingRequestMutationInProgress ? "Deleting..." : "Delete"
        }
        panelMaxWidthClassName="sm:max-w-[40rem]"
      />
    </div>
  );
};

export default AppointmentOverBookingV2Component;
